/**
 * PageCacheManager - Utility for managing page cache operations
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for caching pages, managing cache size,
 * and performing batch cache operations.
 */

import type { PageLayout } from '../../../core/types/Typesetter';
import { generatePageCacheKey } from './CacheKeyGenerator';
import { evictOldestIfNeeded } from './CacheSizeManager';

/**
 * Cache a single page
 * 
 * @param cache - Page cache Map
 * @param docHash - Document hash
 * @param page - Page layout to cache
 */
export function cachePage(
  cache: Map<string, PageLayout>,
  docHash: string,
  page: PageLayout
): void {
  const cacheKey = generatePageCacheKey(docHash, page.page_index);
  cache.set(cacheKey, page);
}

/**
 * Cache multiple pages with optional size management
 * 
 * @param cache - Page cache Map
 * @param docHash - Document hash
 * @param pages - Array of page layouts to cache
 * @param maxSize - Maximum cache size (optional, for eviction)
 */
export function cachePages(
  cache: Map<string, PageLayout>,
  docHash: string,
  pages: PageLayout[],
  maxSize?: number
): void {
  for (const page of pages) {
    cachePage(cache, docHash, page);
    
    // Evict oldest if cache size limit is specified and exceeded
    if (maxSize !== undefined) {
      evictOldestIfNeeded(cache, maxSize);
    }
  }
}

/**
 * Get cached page by index
 * 
 * @param cache - Page cache Map
 * @param docHash - Document hash
 * @param pageIndex - Page index
 * @returns Cached page layout or undefined
 */
export function getCachedPage(
  cache: Map<string, PageLayout>,
  docHash: string,
  pageIndex: number
): PageLayout | undefined {
  const cacheKey = generatePageCacheKey(docHash, pageIndex);
  return cache.get(cacheKey);
}

/**
 * Remove cached page by index
 * 
 * @param cache - Page cache Map
 * @param docHash - Document hash
 * @param pageIndex - Page index
 * @returns True if page was removed, false if it didn't exist
 */
export function removeCachedPage(
  cache: Map<string, PageLayout>,
  docHash: string,
  pageIndex: number
): boolean {
  const cacheKey = generatePageCacheKey(docHash, pageIndex);
  return cache.delete(cacheKey);
}

/**
 * Check if page is cached
 * 
 * @param cache - Page cache Map
 * @param docHash - Document hash
 * @param pageIndex - Page index
 * @returns True if page is cached
 */
export function isPageCached(
  cache: Map<string, PageLayout>,
  docHash: string,
  pageIndex: number
): boolean {
  const cacheKey = generatePageCacheKey(docHash, pageIndex);
  return cache.has(cacheKey);
}

